import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { type Campaign, visualChainLockedToOtherLaunchedCampaign } from '../../domain/mailingDomain';
import {
  useSmsPageDesign,
  smsDialogPaperSx,
  smsPageTokens,
  smsAssignScenarioButtonSx,
  smsTriggerChipSx,
} from '../../theme/smsPageDesign';
import { alpha, useTheme } from '@mui/material/styles';
import { apiPost } from '../../api/client';
import type { MailingCampaign, MailingChannel } from '../mailing-constructor/mailingTypes';
import { campaignChannelsUsed } from '../mailing-constructor/mailingTypes';
import i18next from '../../i18n/initI18n';
import { useTranslation } from 'react-i18next';

export type LinkScenarioDialogProps = {
  open: boolean;
  onClose: () => void;
  campaign: Campaign;
  /** Для проверки: цепочку нельзя забрать у другой кампании после запуска. */
  allCampaigns: Campaign[];
  localFlows: MailingCampaign[];
  /** После успешной привязки (на сервере синхронизируется сценарий с тем же порядком каналов). */
  onLinked: (detail: { flowName: string; campaignName: string }) => void;
  /** Пустой список цепочек: создать новую, привязать к кампании и перейти в редактор. */
  onCreateNewLinkedChain?: () => Promise<void>;
};

function localBadge(chs: MailingChannel[]): string {
  if (!chs.length) return i18next.t('common.emptyPath');
  const slug = chs.join('-');
  return slug.length > 40 ? `${slug.slice(0, 37)}…` : slug;
}

const LinkScenarioDialog: React.FC<LinkScenarioDialogProps> = ({
  open,
  onClose,
  campaign,
  allCampaigns,
  localFlows,
  onLinked,
  onCreateNewLinkedChain,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isSmsLight } = useSmsPageDesign();
  const pageTokens = smsPageTokens(isSmsLight);
  const [loadingKey, setLoadingKey] = React.useState<string | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  const visualId = campaign.settings?.visualMailingId ?? null;

  const currentLinkedLabel = React.useMemo(() => {
    if (!visualId) return '';
    const f = localFlows.find((x) => x.id === visualId);
    return (f?.name && f.name.trim()) || visualId;
  }, [visualId, localFlows]);

  const [confirmSwitchTo, setConfirmSwitchTo] = React.useState<MailingCampaign | null>(null);

  const visibleFlows = React.useMemo(() => {
    return localFlows.filter((m) => {
      const active = visualId === m.id;
      const lockedElsewhere = visualChainLockedToOtherLaunchedCampaign(m.id, campaign.id, allCampaigns);
      return active || !lockedElsewhere;
    });
  }, [localFlows, visualId, campaign.id, allCampaigns]);

  React.useEffect(() => {
    if (!open) {
      setConfirmSwitchTo(null);
      setErr(null);
    }
  }, [open]);

  const linkVisual = async (m: MailingCampaign) => {
    setErr(null);
    setLoadingKey(`v:${m.id}`);
    try {
      await apiPost<{ ok: boolean }>(`/api/campaigns/${campaign.id}/link-visual-chain`, { visualMailingId: m.id });
      onLinked({ flowName: m.name, campaignName: campaign.name });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoadingKey(null);
    }
  };

  const requestLinkOrConfirm = (m: MailingCampaign) => {
    if (visualId && visualId !== m.id) {
      setConfirmSwitchTo(m);
      return;
    }
    void linkVisual(m);
  };

  const linkedRowButtonSx = (active: boolean) => ({
    borderRadius: '20px',
    textTransform: 'none' as const,
    minWidth: 110,
    ...(active
      ? {
          '&.Mui-disabled': {
            opacity: 1,
            cursor: 'default',
            borderColor: theme.palette.success.main,
            color: theme.palette.success.main,
            WebkitTextFillColor: theme.palette.success.main,
            bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'light' ? 0.12 : 0.2),
          },
        }
      : smsAssignScenarioButtonSx()),
  });

  const handleMainClose = () => {
    if (confirmSwitchTo) {
      setConfirmSwitchTo(null);
      return;
    }
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={handleMainClose} maxWidth="sm" fullWidth PaperProps={{ sx: smsDialogPaperSx(isSmsLight) }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {t('linkScenario.dialogTitle')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 400, mb: err ? 1.25 : 1.75, wordBreak: 'break-word', lineHeight: 1.45 }}
        >
          {campaign.name}
        </Typography>
        {err ? (
          <Typography
            color="error"
            variant="body2"
            sx={{ mb: 1.75, display: 'block', wordBreak: 'break-word', lineHeight: 1.45 }}
          >
            {err}
          </Typography>
        ) : null}

        <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
          {t('linkScenario.visualSection')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {visibleFlows.length > 0
            ? visibleFlows.map((m) => {
                const chs = campaignChannelsUsed(m.nodes);
                const active = visualId === m.id;
                const chipSx = smsTriggerChipSx(theme, false);
                const lockedElsewhere = visualChainLockedToOtherLaunchedCampaign(m.id, campaign.id, allCampaigns);
                return (
                  <Box
                    key={m.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap',
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: pageTokens.row,
                      border: 1,
                      borderColor: pageTokens.rowBorder,
                    }}
                  >
                    <Chip label={localBadge(chs)} size="small" sx={chipSx} />
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography fontWeight={600}>{m.name}</Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={active || Boolean(loadingKey)}
                      onClick={() => requestLinkOrConfirm(m)}
                      sx={linkedRowButtonSx(active)}
                    >
                      {loadingKey === `v:${m.id}` ? (
                        <CircularProgress size={18} color={active ? 'success' : 'inherit'} />
                      ) : active ? (
                        t('linkScenario.linked')
                      ) : (
                        t('linkScenario.select')
                      )}
                    </Button>
                  </Box>
                );
              })
            : localFlows.length === 0 ? (
            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                {t('linkScenario.noVisualChains')}
              </Typography>
              {onCreateNewLinkedChain ? (
                <Button
                  variant="outlined"
                  size="medium"
                  disabled={Boolean(loadingKey) || campaign.locked}
                  onClick={async () => {
                    setLoadingKey('__create__');
                    setErr(null);
                    try {
                      await onCreateNewLinkedChain();
                      onClose();
                    } catch (e) {
                      setErr((e as Error).message);
                    } finally {
                      setLoadingKey(null);
                    }
                  }}
                  startIcon={
                    loadingKey === '__create__' ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LinkOutlinedIcon />
                    )
                  }
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    ...smsAssignScenarioButtonSx(),
                  }}
                >
                  {t('linkScenario.createChainOpenEditor')}
                </Button>
              ) : null}
            </Box>
            ) : (
            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                {t('linkScenario.noSelectableChains')}
              </Typography>
              {onCreateNewLinkedChain ? (
                <Button
                  variant="outlined"
                  size="medium"
                  disabled={Boolean(loadingKey) || campaign.locked}
                  onClick={async () => {
                    setLoadingKey('__create__');
                    setErr(null);
                    try {
                      await onCreateNewLinkedChain();
                      onClose();
                    } catch (e) {
                      setErr((e as Error).message);
                    } finally {
                      setLoadingKey(null);
                    }
                  }}
                  startIcon={
                    loadingKey === '__create__' ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LinkOutlinedIcon />
                    )
                  }
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    ...smsAssignScenarioButtonSx(),
                  }}
                >
                  {t('linkScenario.createChainOpenEditor')}
                </Button>
              ) : null}
            </Box>
            )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleMainClose} sx={{ borderRadius: '20px', textTransform: 'none', ...smsAssignScenarioButtonSx() }}>
          {t('linkScenario.close')}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={Boolean(confirmSwitchTo)}
      onClose={() => setConfirmSwitchTo(null)}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={Boolean(loadingKey)}
      PaperProps={{ sx: smsDialogPaperSx(isSmsLight) }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {t('linkScenario.confirmSwitchTitle')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          {confirmSwitchTo
            ? t('linkScenario.confirmSwitchBody', {
                campaign: campaign.name,
                current: currentLinkedLabel,
                next: confirmSwitchTo.name,
              })
            : null}
        </Typography>
        {confirmSwitchTo &&
        visualChainLockedToOtherLaunchedCampaign(confirmSwitchTo.id, campaign.id, allCampaigns) ? (
          <Typography variant="body2" color="error" sx={{ mt: 2, lineHeight: 1.5 }}>
            {t('linkScenario.flowLockedByLaunchedCampaign')}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={() => setConfirmSwitchTo(null)}
          disabled={Boolean(loadingKey)}
          sx={{ borderRadius: '20px', textTransform: 'none', ...smsAssignScenarioButtonSx() }}
        >
          {t('linkScenario.confirmSwitchCancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            Boolean(loadingKey) ||
            !confirmSwitchTo ||
            (confirmSwitchTo
              ? visualChainLockedToOtherLaunchedCampaign(confirmSwitchTo.id, campaign.id, allCampaigns)
              : false)
          }
          onClick={() => {
            if (!confirmSwitchTo) return;
            const next = confirmSwitchTo;
            setConfirmSwitchTo(null);
            void linkVisual(next);
          }}
          sx={{ borderRadius: '20px', textTransform: 'none' }}
        >
          {t('linkScenario.confirmSwitchConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default LinkScenarioDialog;
